import { useAdDetailsContext } from '../../../../../../contexts/AdDetailsContext';

import { fireToast } from '../../../../../../helpers/Toasts';
import { rg4js } from '../../../../../../helpers/raygun';
import { PAGE } from '../../../../../../helpers/pages';
import { pushToDataLayer } from '../../../../../../services/gtmService';
import { useGetWhatsAppLink } from './useGetWhatsAppLink';
import { usePricingIndicatorLeadTracking } from '../../../../../PricingIndicator/usePricingIndicatorLeadTracking';
import { SELLER_TYPES } from '../../../types';

const useContactDealershipBottomSheet = (args: {
  adId: number;
  sectionName?: string;
  price?: string | null;
}) => {
  const { adId, sectionName, price } = args;

  const { isContactSellerModalOpen, setIsContactSellerModalOpen } =
    useAdDetailsContext();

  const {
    data: cachedWhatsAppLink,
    refetch: getWhatsAppLink,
    isFetching,
  } = useGetWhatsAppLink(adId, { enabled: false });

  const { trackPricingIndicatorLead } = usePricingIndicatorLeadTracking({
    page: PAGE.AD_DETAILS,
  });

  const onWhatsAppButtonClick = async () => {
    pushToDataLayer({ event: 'whatsapp_click' });
    trackPricingIndicatorLead({
      sectionName,
      sellerType: SELLER_TYPES.DEALER,
      price,
    });

    // Using window.open instead of router.push to prevent navigation from killing
    // the in-flight Convert SDK tracking request. Revert to router.push once the
    // pricing indicator experiment is over.
    if (cachedWhatsAppLink) {
      window.open(cachedWhatsAppLink, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const response = await getWhatsAppLink();

      if (response.error) {
        // Don't fire a toast, this is already handled in the React Query Wrapper
        rg4js('send', {
          error: response.error,
          customData: {
            pageName: PAGE.AD_DETAILS,
            functionName: 'onWhatsAppButtonClick',
          },
        });
        return;
      }

      if (!response.data) {
        throw new Error('No WhatsApp link found');
      }
      window.open(response.data, '_blank', 'noopener,noreferrer');
    } catch (error) {
      rg4js('send', {
        error:
          error instanceof Error
            ? error
            : new Error('Unknown error while getting WhatsApp link'),
        customData: {
          pageName: PAGE.AD_DETAILS,
          functionName: 'onWhatsAppButtonClick',
        },
      });
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
  };

  const closeContactSellerModal = () => setIsContactSellerModalOpen(false);

  return {
    isContactSellerModalOpen,
    onWhatsAppButtonClick,
    isLoading: isFetching,
    closeContactSellerModal,
  };
};

export { useContactDealershipBottomSheet };
